// =============================================================================
// HoursReportsListScreen — /reports/hours  (T-370)
// Saved client hours reports: actual vs reported + variance at a glance, with
// status (draft / sent). Open one to view/edit/export, or start a new one.
// =============================================================================

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Clock, Plus, AlertTriangle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Button, Pill, Spinner, EmptyState } from "@/components/ui";

const n2 = (n: number) => Math.round(Number(n) * 100) / 100;

export default function HoursReportsListScreen() {
  const user = useRequireAuth();
  const canView = hasRole(user, "manager");

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ["hours_reports_list"],
    enabled: !!user && canView,
    queryFn: async () => {
      const { data, error } = await supabase.from("hours_reports")
        .select(`report_id, period_start, period_end, title, status, group_by,
          actual_hours_total, reported_hours_total, created_at,
          client:client_id ( legal_name )`)
        .order("created_at", { ascending: false }).limit(300);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  if (!user) return null;
  if (!canView) {
    return <div className="p-6 max-w-2xl mx-auto"><Card><EmptyState icon={AlertTriangle} title="Managers only" description="Client hours reports are available to managers and directors." /></Card></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto pb-20">
      <Link to="/reports/man-hours" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14} /> Man-hours
      </Link>
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Clock size={20} className="text-accent" /> Client hours reports
          </h1>
          <p className="text-text-muted text-[13px] mt-1">
            Saved snapshots of actual vs reported hours per project — the record of what was worked and what was sent to the client.
          </p>
        </div>
        <Link to="/reports/hours/new"><Button icon={Plus}>New report</Button></Link>
      </div>

      {isLoading ? (
        <div className="py-16 flex justify-center"><Spinner size={20} /></div>
      ) : reports.length === 0 ? (
        <Card><EmptyState icon={Clock} title="No reports yet" description="Create a client hours report to record actual vs reported hours for a project." /></Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-line">
                  {["Client", "Grouped by", "Month", "Actual", "Reported", "Variance", "Status"].map((h, i) => (
                    <th key={h} className={`px-4 py-3 text-[10px] font-medium uppercase tracking-wider2 text-text-muted ${i >= 3 && i <= 5 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => {
                  const v = n2(Number(r.reported_hours_total) - Number(r.actual_hours_total));
                  return (
                    <tr key={r.report_id} className="border-b border-line hover:bg-ink-hover cursor-pointer">
                      <td className="px-4 py-3">
                        <Link to={`/reports/hours/${r.report_id}`} className="block">
                          <div className="text-[13px] text-text">{r.client?.legal_name ?? "—"}</div>
                          {r.title && <div className="text-[12px] text-text-muted truncate max-w-[220px]">{r.title}</div>}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-[12px] text-text-muted">{r.group_by === "carrier" ? "Carrier" : "Asset owner"}</td>
                      <td className="px-4 py-3 text-[12px] text-text-muted">{new Date(r.period_start + "T00:00:00").toLocaleString("en-AU", { month: "short", year: "numeric" })}</td>
                      <td className="px-4 py-3 text-right font-mono text-text-muted">{n2(r.actual_hours_total)}</td>
                      <td className="px-4 py-3 text-right font-mono text-text font-semibold">{n2(r.reported_hours_total)}</td>
                      <td className={`px-4 py-3 text-right font-mono text-[12px] ${v > 0 ? "text-warn" : v < 0 ? "text-danger" : "text-text-dim"}`}>{v > 0 ? "+" : ""}{v || "0"}</td>
                      <td className="px-4 py-3"><Pill label={r.status === "sent" ? "Sent" : "Draft"} tone={r.status === "sent" ? "won" : "pending"} small /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
